import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, Typography, Card, CardContent, Chip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  PlayCircle as PlayIcon,
  SchoolOutlined as CourseIcon,
  CheckCircleOutline as CompletedIcon,
} from "@mui/icons-material";
import PageContainer from "../../components/PageContainer";
import PageSection from "../../components/PageSection";
import VideoPlayerModal from "../../components/VideoPlayerModal";
import NoticeboardButton from "../../components/shared/NoticeboardButton";
import { lightTheme } from "../../styles/theme";
import { HELP_VIDEOS, videoToPlay } from "../../domain/help/helpContent";
import { COURSE_LENGTH, COURSE_PATH } from "../../domain/help/course";
import { CreatePlanCourseCard } from "./CreatePlanCourse";
import FundamentalsBanner from "./FundamentalsBanner";
import { useCourseProgress } from "../../hooks/useCourseProgress";

const VIDEO_COLORS: Record<string, string> = {
  "create-event": lightTheme.colors.primaryAltDark,
  "add-user": lightTheme.colors.primaryAltDark,
};

const VideosHelp: React.FC = () => {
  const theme = useTheme();
  const { nextUp } = useCourseProgress();
  const location = useLocation();
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);

  // Ask Noticeboard can open this page playing a video, even when it's already open
  useEffect(() => {
    const video = videoToPlay(location.state);
    if (video) setSelectedVideo(video);
  }, [location]);

  const videoTutorials = HELP_VIDEOS.map((video) => ({
    ...video,
    color: VIDEO_COLORS[video.id] ?? lightTheme.colors.primary,
  }));

  const handleVideoPlay = (videoPath: string, title: string) => {
    setSelectedVideo({ videoPath, title });
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <PageContainer>
      <FundamentalsBanner />

      {/* Header */}
      <PageSection sx={{ mb: 4 }}>
        <Box
          sx={{
            borderRadius: { xs: 2, md: 3 },
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: 4,
            mt: { xs: 2, md: 0 },
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.14),
            background: `radial-gradient(circle at 85% 15%, ${alpha(
              theme.palette.primary.main,
              0.12
            )} 0%, transparent 45%), linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
            alignItems: "center",
            gap: { xs: 2, md: 3 },
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: { xs: 16, md: 18 },
              left: { xs: 14, md: 22 },
              color: "primary.main",
              opacity: 0.45,
              fontSize: { xs: "1.05rem", md: "1.25rem" },
              lineHeight: 1,
              pointerEvents: "none",
            }}
          >
            ✦
          </Box>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: { xs: 30, md: 38 },
              left: { xs: 34, md: 48 },
              color: "primary.main",
              opacity: 0.33,
              fontSize: { xs: "0.8rem", md: "0.95rem" },
              lineHeight: 1,
              pointerEvents: "none",
            }}
          >
            ✧
          </Box>
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
              zIndex: 1,
              maxWidth: { xs: "100%", md: 520 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "2rem", md: "2.7rem" },
                lineHeight: { xs: 1.08, md: 1.04 },
                fontWeight: 400,
                letterSpacing: "-0.015em",
                color: "text.primary",
              }}
            >
              <Box component="span" sx={{ color: "primary.main" }}>
                Tutorials
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 1.25,
                fontSize: { xs: "1rem", md: "1.08rem" },
                lineHeight: 1.35,
                color: "text.secondary",
                maxWidth: 560,
              }}
            >
              Follow step-by-step lessons and videos to learn how to use Noticeboard
              effectively.
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontSize: { xs: "0.98rem", md: "1rem" },
                lineHeight: 1.45,
                color: "text.secondary",
                maxWidth: 560,
              }}
            >
              Learn core workflows quickly and make sure your communication reaches
              everyone in style.
            </Typography>
            <Box
              sx={{
                mt: 2.25,
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <NoticeboardButton
                variant="primary"
                onClick={() => {
                  const el = document.getElementById("tutorials-course");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1,
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                Browse Tutorials
              </NoticeboardButton>
              <Typography sx={{ fontSize: "0.95rem", color: "text.secondary" }}>
                Learn in minutes
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              minHeight: 190,
            }}
          >
            <Box
              component="img"
              src="/icons/bookademo.webp"
              alt="Tutorials preview"
              loading="lazy"
              decoding="async"
              sx={{
                width: { md: 248, lg: 297 },
                maxWidth: "100%",
                objectFit: "contain",
                filter: `drop-shadow(0 8px 18px ${alpha(
                  theme.palette.common.black,
                  0.16
                )})`,
              }}
            />
          </Box>
        </Box>
      </PageSection>

      {/* Course */}
      <PageSection sx={{ mb: 4 }}>
        <Typography id="tutorials-course" component="h2" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Courses
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 3,
          }}
        >
          {/* Noticeboard Fundamentals */}
          <Card
            elevation={0}
            component={RouterLink}
            to={COURSE_PATH}
            sx={{
              display: "block",
              textDecoration: "none",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
              bgcolor: "card",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: `0 4px 20px ${lightTheme.colors.primaryTint15}`,
                transform: "translateY(-2px)",
              },
            }}
          >
            <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  height: 160,
                  borderRadius: 2,
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.14),
                  background: `linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
                }}
              >
                <CourseIcon aria-hidden sx={{ fontSize: 44, color: "primary.main" }} />
                <Typography sx={{ fontSize: "1.15rem", color: "text.primary" }}>
                  Noticeboard{" "}
                  <Box component="span" sx={{ color: "primary.main" }}>
                    Fundamentals
                  </Box>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: "primary.main" }}>
                  Noticeboard Fundamentals
                </Typography>
                {!nextUp && <Chip label="Completed" icon={<CompletedIcon />} color="success" size="small" variant="outlined" />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {COURSE_LENGTH} short lessons, from setting up your Organization to sending your
                first Notice.
              </Typography>
            </CardContent>
          </Card>
          <CreatePlanCourseCard />
        </Box>
      </PageSection>

      {/* Videos */}
      <PageSection>
        <Typography component="h2" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Videos
        </Typography>
        <Box
          id="video-tutorials-grid"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 3,
          }}
        >
          {videoTutorials.map((video) => (
            <Box key={video.id}>
              <Card
                elevation={0}
                onClick={() => handleVideoPlay(video.videoPath, video.title)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  height: "100%",
                  bgcolor: "card",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: `0 4px 20px ${lightTheme.colors.primaryTint15}`,
                    transform: "translateY(-2px)",
                    "& .play-button-overlay": {
                      opacity: 1,
                    },
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Video Thumbnail/Icon */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 160,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      component="img"
                      src={video.thumbnail}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: theme.palette.textShadows.dark,
                        zIndex: 1,
                      }}
                    />
                    {/* Play Button Overlay */}
                    <Box
                      className="play-button-overlay"
                      sx={{
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: "50%",
                          bgcolor: theme.palette.whiteOverlays.overlay90,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 4px 20px ${theme.palette.textShadows.medium}`,
                        }}
                      >
                        <PlayIcon sx={{ fontSize: 32, color: video.color }} />
                      </Box>
                    </Box>

                    {/* Video Title on Thumbnail */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 2,
                        background:
                          `linear-gradient(transparent, ${theme.palette.ui.backdropStrong})`,
                        p: 2,
                        borderRadius: "0 0 8px 8px",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "common.white",
                          fontWeight: 700,
                          fontSize: "1rem",
                          textAlign: "center",
                          textShadow: `0 2px 4px ${lightTheme.colors.blackOverlay80}`,
                          lineHeight: 1.2,
                        }}
                      >
                        {video.title}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Video Info */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        color: video.color,
                        mb: 1,
                      }}
                    >
                      {video.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        lineHeight: 1.6,
                      }}
                    >
                      {video.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </PageSection>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayerModal
          open={!!selectedVideo}
          onClose={handleCloseVideo}
          videoPath={selectedVideo.videoPath}
          title={selectedVideo.title}
        />
      )}
    </PageContainer>
  );
};

export default VideosHelp;
